import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Download, ExternalLink, Search, X } from "lucide-react";
import { api } from "../../api";
import { formatCurrency } from "../../lib/format";
import { GymLoader } from "./GymLoader";
import { ErrorState } from "./ErrorState";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

// The records behind a KPI tile. One generic table serves every metric: the backend ships the
// column list with the rows, so a drill-down gains a column the moment its builder does, and the
// frontend never holds a second, drifting opinion about what a metric contains.
//
// It also carries the metric's DEFINITION. A number without its definition is where dashboards
// lose trust — "dues outstanding" nets differently from "who owes us money", and the owner
// deserves to read that where they're looking at the rows, not in a doc somewhere.

function formatCell(value, type) {
  if (value === null || value === undefined || value === "") return "—";
  switch (type) {
    case "currency":
      return formatCurrency(value);
    case "number":
      return typeof value === "number" ? new Intl.NumberFormat("en-IN").format(value) : value;
    case "date":
      return typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)
        ? new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
        : value;
    case "datetime":
      return typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)
        ? new Date(value).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
        : value;
    default:
      return String(value);
  }
}

const NUMERIC = new Set(["currency", "number"]);

function toCsv(columns, rows) {
  const esc = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = columns.map((c) => esc(c.label)).join(",");
  // Raw values, not display strings: a CSV is for spreadsheets, where ₹ and "12 Sep" are worse
  // than the underlying number and ISO date. Money is converted paise -> rupees so the column
  // sums to the same figure shown on screen.
  const body = rows.map((r) => columns.map((c) => esc(c.type === "currency" ? (r[c.key] || 0) / 100 : r[c.key])).join(","));
  return [header, ...body].join("\n");
}

// Two sources, one panel. `metric` fetches from /reports/drilldown; `localData` renders a payload
// the page already has in hand — which is how screens whose permission set doesn't include
// pnl.view still get a real drill-down, without widening what that endpoint exposes.
export function DrillDown({ metric, branchId, period, link, localData, onClose }) {
  const [sort, setSort] = useState({ key: null, dir: "desc" });
  const [q, setQ] = useState("");

  const query = useQuery({
    queryKey: ["drilldown", metric, branchId, period?.period_start, period?.period_end],
    queryFn: async () => (await api.get(`/reports/drilldown/${metric}`, {
      // The tile's own period, passed straight through — so a period-sensitive drill-down
      // covers the identical window the number on the tile was computed over.
      params: { branch_id: branchId || undefined, ...(period || {}) },
    })).data,
    enabled: !!metric && !localData,
  });

  const data = localData || query.data;
  const loading = !localData && query.isLoading;
  const errored = !localData && query.isError;

  const rows = useMemo(() => {
    if (!data) return [];
    let out = data.rows;
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      out = out.filter((r) => data.columns.some((c) => String(r[c.key] ?? "").toLowerCase().includes(needle)));
    }
    if (sort.key) {
      const col = data.columns.find((c) => c.key === sort.key);
      const numeric = col && NUMERIC.has(col.type);
      out = [...out].sort((a, b) => {
        const av = a[sort.key], bv = b[sort.key];
        if (av === bv) return 0;
        if (av === null || av === undefined) return 1;   // blanks sink, either direction
        if (bv === null || bv === undefined) return -1;
        const cmp = numeric ? Number(av) - Number(bv) : String(av).localeCompare(String(bv));
        return sort.dir === "asc" ? cmp : -cmp;
      });
    }
    return out;
  }, [data, q, sort]);

  const download = () => {
    const blob = new Blob([toCsv(data.columns, rows)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${metric || data.metric || "records"}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }));

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6"
      style={{ background: "rgba(10,10,14,0.55)", backdropFilter: "blur(3px)" }}
      onClick={onClose}
      data-testid="drilldown-overlay"
    >
      <div
        className="solid-surface w-full max-w-[1200px] rounded-t-[18px] sm:rounded-[18px] flex flex-col"
        style={{ maxHeight: "92vh" }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={data?.title || "Details"}
        data-testid="drilldown-panel"
      >
        <div className="flex items-start justify-between gap-4 p-5 pb-3" style={{ borderBottom: "1px solid var(--scarlet-hairline)" }}>
          <div className="min-w-0">
            <h2 className="text-lg font-bold" style={{ color: "var(--scarlet-text)" }}>
              {data?.title || "Loading…"}
            </h2>
            {data && (
              <p className="text-xs mt-1 leading-relaxed" style={{ color: "var(--scarlet-text-secondary)", maxWidth: "72ch" }}>
                {data.definition}
              </p>
            )}
          </div>
          <button onClick={onClose} className="shrink-0 p-1.5 rounded-[8px] hover:opacity-70" aria-label="Close" data-testid="drilldown-close">
            <X size={18} style={{ color: "var(--scarlet-text-secondary)" }} />
          </button>
        </div>

        {loading && <div className="p-8"><GymLoader label="Loading details…" /></div>}
        {errored && <div className="p-6"><ErrorState error={query.error} onRetry={() => query.refetch()} title="Couldn't load these records" /></div>}

        {data && (
          <>
            <div className="flex items-center gap-3 flex-wrap px-5 py-3">
              <div className="relative flex-1 min-w-[180px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--scarlet-text-secondary)" }} />
                <Input
                  value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search these records…"
                  className="pl-8 h-9" data-testid="drilldown-search"
                />
              </div>
              <div className="text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>
                <span className="font-semibold" style={{ color: "var(--scarlet-text)" }}>{rows.length}</span>
                {rows.length !== data.row_count ? ` of ${data.row_count}` : ""} row{rows.length === 1 ? "" : "s"}
                {" · "}{data.total_label}:{" "}
                <span className="font-semibold tabular-nums" style={{ color: "var(--scarlet-text)" }}>
                  {data.total_format === "currency" ? formatCurrency(data.total) : data.total}
                </span>
              </div>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={download} disabled={!rows.length} data-testid="drilldown-export">
                <Download size={14} /> CSV
              </Button>
              {link && (
                <Button variant="outline" size="sm" className="gap-1.5" onClick={link.onClick} data-testid="drilldown-link">
                  <ExternalLink size={14} /> {link.label}
                </Button>
              )}
            </div>

            <div className="overflow-auto px-5 pb-5" style={{ flex: 1 }}>
              {rows.length === 0 ? (
                <p className="text-sm text-center py-10" style={{ color: "var(--scarlet-text-secondary)" }}>
                  {data.row_count === 0 ? "Nothing recorded for this yet." : "No rows match that search."}
                </p>
              ) : (
                <table className="w-full text-sm" style={{ minWidth: "max-content" }}>
                  <thead className="sticky top-0" style={{ background: "var(--scarlet-surface)" }}>
                    <tr style={{ borderBottom: "1px solid var(--scarlet-hairline)" }}>
                      {data.columns.map((c) => (
                        <th
                          key={c.key}
                          onClick={() => toggleSort(c.key)}
                          className="px-3 py-2.5 text-xs font-semibold whitespace-nowrap cursor-pointer select-none hover:opacity-75"
                          style={{ color: "var(--scarlet-text-secondary)", textAlign: NUMERIC.has(c.type) ? "right" : "left" }}
                        >
                          <span className="inline-flex items-center gap-1">
                            {c.label}
                            {sort.key === c.key && (sort.dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid var(--scarlet-hairline)" }}>
                        {data.columns.map((c) => {
                          const numeric = NUMERIC.has(c.type);
                          const negative = numeric && Number(r[c.key]) < 0;
                          return (
                            <td
                              key={c.key}
                              className={`px-3 py-2.5 whitespace-nowrap ${numeric ? "tabular-nums" : ""}`}
                              style={{
                                textAlign: numeric ? "right" : "left",
                                color: negative ? "var(--scarlet-neg)" : "var(--scarlet-text)",
                              }}
                            >
                              {formatCell(r[c.key], c.type)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
